"use client";

/* eslint-disable @next/next/no-img-element */

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import LinkButton from "./_atoms/linkbutton";
import { IMenuItem } from "@/types/strapi-menu";
import { getImageUrl } from "@/utils/images";
import clsx from "clsx";

const getHref = (item: IMenuItem, child: IMenuItem) => {
	return child.slug.includes("landing")
		? child.slug.split("/landing")[1]
		: `${item.slug}${child.slug}`;
};

const getMenuItemCategory = (item: IMenuItem): string | null => {
	const { category } = item;

	if (!category) return null;

	if (typeof category === "string") {
		const trimmed = category.trim();
		return trimmed.length ? trimmed : null;
	}

	if (typeof category === "object") {
		const name = category?.name?.trim();
		return name && name.length ? name : "Autre";
	}

	return null;
};

const groupMenuItemsByCategory = (
	items: IMenuItem[],
): Record<string, IMenuItem[]> => {
	return items.reduce(
		(acc, item) => {
			const category = getMenuItemCategory(item) ?? "Autre";

			if (!acc[category]) {
				acc[category] = [];
			}

			acc[category].push(item);
			return acc;
		},
		{} as Record<string, IMenuItem[]>,
	);
};

const sortCategoryEntries = (
	entries: [string, IMenuItem[]][],
): [string, IMenuItem[]][] => {
	const normalize = (value: string) => value.trim().toLowerCase();
	const getPriority = (category: string) => {
		const normalized = normalize(category);

		if (normalized === "site web") {
			return -1;
		}

		if (normalized === "autre" || normalized === "autres") {
			return 1;
		}

		return 0;
	};

	return entries
		.map((entry, index) => ({ entry, index }))
		.sort((a, b) => {
			const priorityDifference =
				getPriority(a.entry[0]) - getPriority(b.entry[0]);

			if (priorityDifference !== 0) {
				return priorityDifference;
			}

			return a.index - b.index;
		})
		.map((item) => item.entry);
};

export default function Navbar({ items }: { items: IMenuItem[] }) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [highlight, setHighlight] = useState({
		left: 0,
		top: 0,
		width: 0,
		height: 0,
		opacity: 0,
	});
	const [open, setOpen] = useState(false);
	const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
	const [animateIn, setAnimateIn] = useState(false);
	const [hideNav, setHideNav] = useState(false);
	const [hoveredItem, setHoveredItem] = useState<IMenuItem | null>(null);

	const lastScrollY = useRef(0);

	const move = (target: HTMLElement | null) => {
		if (!target || !containerRef.current) return;
		const itemRect = target.getBoundingClientRect();
		const parentRect = containerRef.current.getBoundingClientRect();

		requestAnimationFrame(() => {
			setHighlight({
				left: itemRect.left - parentRect.left,
				top: itemRect.top - parentRect.top,
				width: itemRect.width,
				height: itemRect.height,
				opacity: 1,
			});
		});
	};

	const clear = () => {
		setHighlight((u) => ({ ...u, opacity: 0 }));
		setHoveredItem(null);
	};

	useEffect(() => {
		setTimeout(() => setAnimateIn(true), 50);

		const handleScroll = () => {
			const currentScrollY = window.scrollY;

			if (currentScrollY > 500 && currentScrollY > lastScrollY.current) {
				setHideNav(true); // Scroll vers le bas → cacher
			} else {
				setHideNav(false); // Scroll vers le haut → montrer
			}

			lastScrollY.current = currentScrollY;
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		if (typeof document === "undefined") return;

		if (open) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}

		return () => {
			document.body.style.overflow = "";
		};
	}, [open]);

	const renderLabel = (item: IMenuItem) => {
		return (
			<>
				{item.icon && (
					<img
						src={getImageUrl(item.icon.url)}
						alt={item.label}
						role="presentation"
						aria-hidden="true"
						className="h-5 w-5 object-contain"
					/>
				)}
				{item.description ? (
					<div>
						<strong>{item.label}</strong>
						<br />
						<small>{item.description}</small>
					</div>
				) : (
					<span>{item.label}</span>
				)}
			</>
		);
	};

	const handleDesktopEnter = (
		item: IMenuItem,
		element: HTMLElement | null,
	) => {
		move(element);
		if (item.children?.length) {
			setHoveredItem(item);
		} else {
			setHoveredItem(null);
		}
	};

	const toggleMobileMenu = () => {
		setOpen((prev) => !prev);
		if (open) {
			setActiveSubmenu(null);
		}
	};

	const hoveredChildren = hoveredItem?.children ?? [];
	const shouldGroupHoveredChildren = hoveredChildren.some((child) =>
		Boolean(getMenuItemCategory(child)),
	);
	const groupedHoveredChildren = shouldGroupHoveredChildren
		? groupMenuItemsByCategory(hoveredChildren)
		: null;

	return (
		<>
			<nav
				className={`fixed max-w-lvw top-0 left-0 right-0 z-50 transition-all duration-500 bg-white/80 backdrop-blur-md ${
					hideNav
						? "-translate-y-full opacity-0"
						: "translate-y-0 opacity-100"
				}`}
			>
				<div
					ref={containerRef}
					onMouseLeave={clear}
					className={`relative container-large flex items-center justify-between py-3 transition-all duration-700 ease-out ${
						animateIn
							? "opacity-100 translate-y-0"
							: "opacity-0 translate-y-2"
					}`}
				>
					<Link href="/">
						<Image
							src="/logonoir.svg"
							alt="logo audrey hossepian développeuse web"
							width={70}
							height={50.63}
						/>
					</Link>
					{/* BURGER ICON */}
					<button
						className="relative z-50 flex h-12 w-12 items-center justify-center md:hidden"
						aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
						aria-expanded={open}
						onClick={toggleMobileMenu}
					>
						<span
							className={`absolute h-0.5 w-6 bg-current transition-transform duration-300 ease-in-out ${
								open
									? "translate-y-0 rotate-45"
									: "-translate-y-2"
							}`}
						/>
						<span
							className={`absolute h-0.5 w-6 bg-current transition-opacity duration-200 ${
								open ? "opacity-0" : "opacity-100"
							}`}
						/>
						<span
							className={`absolute h-0.5 w-6 bg-current transition-transform duration-300 ease-in-out ${
								open
									? "translate-y-0 -rotate-45"
									: "translate-y-2"
							}`}
						/>
					</button>
					{/* NAV ITEMS */}
					<div className="relative hidden items-center gap-6 md:flex">
						<ul className="relative flex items-center gap-4">
							{items
								.filter((item) => item.cta == false)
								.map((item) => {
									const hasChildren = Boolean(
										item.children?.length,
									);

									return (
										<li
											key={item.label}
											className="group relative rounded-lg transition-all duration-200  bg-transparent hover:bg-extended-blue-gray-100 py-2 px-4"
											onMouseEnter={(e) =>
												handleDesktopEnter(
													item,
													e.currentTarget,
												)
											}
										>
											<span className="flex items-center gap-2">
												<Link
													href={`${item.slug}`}
													className="relative z-10 flex items-center gap-2 h-full"
													onFocus={(e) =>
														handleDesktopEnter(
															item,
															e.currentTarget
																.parentElement as HTMLElement,
														)
													}
													onBlur={(e) => {
														const nextTarget =
															e.relatedTarget as HTMLElement | null;
														if (
															nextTarget &&
															containerRef.current?.contains(
																nextTarget,
															)
														) {
															return;
														}
														setHoveredItem(null);
														setHighlight((u) => ({
															...u,
															opacity: 0,
														}));
													}}
													onClick={() =>
														setActiveSubmenu(null)
													}
												>
													{renderLabel(item)}
												</Link>
												{hasChildren && (
													<ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
												)}
											</span>
										</li>
									);
								})}
						</ul>
						<div onMouseEnter={clear} onFocusCapture={clear}>
							{items
								.filter((item) => item.cta === true)
								.map((item) => (
									<LinkButton
										href={item.slug}
										key={item.label}
										text={item.label}
									/>
								))}
						</div>
					</div>
					{hoveredItem?.children?.length ? (
						<div className="absolute left-[50%] -translate-x-1/2 top-full hidden container mx-auto md:block">
							{shouldGroupHoveredChildren &&
							groupedHoveredChildren ? (
								<div
									className={`mt-6 grid gap-6 rounded-2xl border border-neutral/10 bg-white p-6 shadow-xl md:grid-cols-2`}
								>
									{sortCategoryEntries(
										Object.entries(groupedHoveredChildren),
									).map(([category, links], index) => {
										return (
											<div
												key={category}
												className={clsx(
													"min-w-[220px]",
													index === 0 &&
														"md:row-span-2", // 1ère catégorie = col gauche sur 2 lignes
													index === 1 &&
														"md:col-start-2 md:row-start-1", // 2e = col droite, ligne 1
													index === 2 &&
														"md:col-start-2 md:row-start-2", // 3e = col droite, ligne 2
												)}
											>
												<p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
													{category}
												</p>
												<div className="mt-3 flex flex-col gap-2">
													{links.map((child) => {
														const href = getHref(
															hoveredItem,
															child,
														);
														return (
															<Link
																onClick={clear}
																key={`${category}-${child.slug}`}
																target={
																	href ===
																	`${hoveredItem.slug}${child.slug}`
																		? "_self"
																		: "_blank"
																}
																href={href}
																className="flex items-center gap-4 rounded-xl px-4 py-3 transition-colors hover:bg-extended-blue-gray-100"
															>
																{renderLabel(
																	child,
																)}
															</Link>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>
							) : (
								<div className="mt-6 grid grid-cols-2 gap-4 rounded-2xl border border-neutral/10 bg-white p-6 shadow-xl">
									{hoveredItem.children?.map((child) => {
										const href = getHref(
											hoveredItem,
											child,
										);
										return (
											<Link
												onClick={clear}
												key={child.slug}
												href={href}
												target={
													href ===
													`${hoveredItem.slug}${child.slug}`
														? "_self"
														: "_blank"
												}
												className="flex items-center gap-4 rounded-xl px-4 py-3 transition-colors hover:bg-extended-blue-gray-100"
											>
												{renderLabel(child)}
											</Link>
										);
									})}
								</div>
							)}
						</div>
					) : null}
				</div>
			</nav>
			{open && (
				<div className="mobile-menu fixed h-screen w-screen inset-0 z-40 flex flex-col bg-white/80 backdrop-blur-2xl md:hidden">
					<div className="flex flex-1 flex-col items-center justify-center gap-10 px-6 text-center">
						{items.map((item) => {
							const hasChildren = Boolean(item.children?.length);

							if (hasChildren) {
								const isActive = activeSubmenu === item.label;
								const mobileHasCategories = item.children?.some(
									(child) =>
										Boolean(getMenuItemCategory(child)),
								);
								const mobileGroupedChildren =
									mobileHasCategories
										? groupMenuItemsByCategory(
												item.children ?? [],
											)
										: null;

								return (
									<div
										key={item.label}
										className="flex w-full max-w-xs flex-col items-center menu"
									>
										<button
											type="button"
											className="flex items-center gap-3 text-lg"
											onClick={() =>
												setActiveSubmenu((prev) =>
													prev === item.label
														? null
														: item.label,
												)
											}
											aria-expanded={isActive}
										>
											<span className="flex items-center gap-3 uppercase font-semibold tracking-wide">
												{renderLabel(item)}
											</span>
											<ChevronDown
												className={`h-5 w-5 transition-transform duration-300 ${
													isActive ? "rotate-180" : ""
												}`}
											/>
										</button>
										<div
											className={`flex w-full flex-col items-center overflow-hidden transition-all duration-300 ease-in-out submenu ${
												isActive
													? "max-h-96 opacity-100"
													: "max-h-0 opacity-0"
											}`}
										>
											{mobileHasCategories &&
											mobileGroupedChildren ? (
												<div className="flex w-full flex-col gap-6 py-4">
													{sortCategoryEntries(
														Object.entries(
															mobileGroupedChildren,
														),
													).map(
														([category, links]) => (
															<div
																key={`${item.label}-${category}`}
																className="flex w-full flex-col items-center gap-3"
															>
																<p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
																	{category}
																</p>
																<div className="flex w-full flex-col items-center gap-2">
																	{links.map(
																		(
																			child,
																		) => {
																			const href =
																				getHref(
																					item,
																					child,
																				);
																			return (
																				<Link
																					key={`${category}-${child.slug}`}
																					href={
																						href
																					}
																					target={
																						href ===
																						`${item.slug}${child.slug}`
																							? "_self"
																							: "_blank"
																					}
																					className="flex w-full items-center justify-center gap-3 text-base"
																					onClick={() => {
																						setOpen(
																							false,
																						);
																						setActiveSubmenu(
																							null,
																						);
																					}}
																				>
																					{renderLabel(
																						child,
																					)}
																				</Link>
																			);
																		},
																	)}
																</div>
															</div>
														),
													)}
												</div>
											) : (
												item.children?.map((child) => {
													const href = getHref(
														item,
														child,
													);
													return (
														<Link
															key={child.slug}
															href={href}
															target={
																href ===
																`${item.slug}${child.slug}`
																	? "_self"
																	: "_blank"
															}
															className="mt-4 flex w-full items-center justify-center gap-3 border-b pb-3 text-base"
															onClick={() => {
																setOpen(false);
																setActiveSubmenu(
																	null,
																);
															}}
														>
															{renderLabel(child)}
														</Link>
													);
												})
											)}
										</div>
									</div>
								);
							}

							return (
								<Link
									key={item.label}
									href={item.slug ?? "#"}
									className="flex items-center gap-3 text-lg uppercase font-semibold tracking-wide"
									onClick={() => {
										setOpen(false);
										setActiveSubmenu(null);
									}}
								>
									{renderLabel(item)}
								</Link>
							);
						})}
					</div>
					<div className="px-8 pb-10">
						{items
							.filter((item) => item.cta === true)
							.map((item) => (
								<Link
									href={item.slug}
									key={item.slug}
									className="btn btn-neutral w-full uppercase tracking-[0.3em]"
									onClick={() => {
										setOpen(false);
										setActiveSubmenu(null);
									}}
								>
									{renderLabel(item)}
								</Link>
							))}
					</div>
				</div>
			)}
		</>
	);
}
